import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Target, Factory, Award, Users,
  Zap, Shield, Droplets, CheckCircle2, ArrowRight, Package, Wrench, Download,
  BadgePercent, HardHat, Truck, Palette, Warehouse, Calculator
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';


import FlameStrikethrough from '../components/icons/FlameStrikethrough';
import SEOHead, { seoConfig } from '../components/SEOHead';
import { API_URL } from '../config';

const AboutPage = () => {
  const { language } = useLanguage();
  const seo = seoConfig.about[language] || seoConfig.about.en || seoConfig.about.ru;

  // Content translations
  const content = {
    ru: {
      header: {
        title: 'О компании',
        subtitle: 'Первый и специализированный завод по производству гипсовиниловых панелей в Казахстане'
      },
      downloadPresentation: 'Скачать презентацию о компании',
      mission: {
        title: 'Наша миссия',
        text: 'Помогать Казахстану строиться красиво. Делать современный дизайн доступным, быстрым и чистым.',
        subtext: 'Мы верим, что качественная среда влияет на мышление и поведение человека. Вносим вклад в развитие архитектурной культуры и стандартов нового времени.'
      },
      gallery: {
        title: 'Наше производство и проекты',
        items: [
          {
            image: 'https://images.unsplash.com/photo-1764185800646-f75f7e16e465?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxmYWN0b3J5JTIwcHJvZHVjdGlvbiUyMGxpbmUlMjBtYW51ZmFjdHVyaW5nfGVufDB8fHx8MTc3NDQ0MDM5OHww&ixlib=rb-4.1.0&q=85',
            title: 'Производственная линия',
            subtitle: 'СЭЗ Астана'
          },
          {
            image: 'https://images.unsplash.com/photo-1750189953388-5ef155c12369?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBpbnRlcmlvciUyMGRlc2lnbiUyMGNsZWFuJTIwd2FsbHN8ZW58MHx8fHwxNzc0NDQwMzk0fDA&ixlib=rb-4.1.0&q=85',
            title: 'Коридоры и холлы',
            subtitle: 'Готовые решения'
          },
          {
            image: 'https://images.pexels.com/photos/245219/pexels-photo-245219.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
            title: 'Офисные пространства',
            subtitle: 'Современный дизайн'
          }
        ]
      },
      advantages: {
        title: 'Почему выбирают нас',
        items: [
          { icon: Factory, title: 'Собственное производство', text: 'Полный цикл в СЭЗ Астана без посредников. Сертификат СТ KZ, реестр КазНИИСА.' },
          { icon: Award, title: 'Лидеры отрасли', text: 'Первый бренд, работающий исключительно в нише гипсовиниловых панелей.' },
          { icon: Shield, title: 'Сертифицированная продукция', text: 'Безопасно, одобрено СЭС и пожарными службами. Класс Г1.' },
          { icon: Users, title: 'Сервис 360°', text: 'От консультации до шеф-монтажа и визуализации помещений.' },
          { icon: Package, title: 'Готовность к большим проектам', text: '2000 м² за 24 часа. Более 5000 м² на складе.' },
          { icon: Wrench, title: 'Проектный отдел', text: 'Просчитаем ваш объект без лишних трат.' }
        ]
      },
      product: {
        title: 'Инновационный материал',
        subtitle: 'Технологичная альтернатива устаревшим методам отделки',
        description: 'Гипсовиниловые панели — это сочетание слабогорючей плиты и износостойкого, моющегося декоративного ПВХ покрытия.',
        features: [
          { icon: Zap, text: 'Ускоряет отделку в 5 раз' },
          { icon: 'FlameStrikethrough', text: 'Класс горючести Г1 (слабогорючие)' },
          { icon: Droplets, text: 'Влагостойкость — применимы в бассейнах' },
          { icon: CheckCircle2, text: 'Одобрено СЭС, моющееся покрытие' }
        ],
        replaces: 'Заменяет: покраску, штукатурку, шпаклевку, бамбуковые панели, ЛДСП/МДФ, крашеный ГКЛ',
        height: 'Высота панелей до 3 м — минимум стыков, максимум эстетики'
      },
      stats: [
        { number: '1 млн', unit: 'м²', label: 'производственная мощность в год' },
        { number: '2 000', unit: 'м²', label: 'изготовление за сутки' },
        { number: '100+', unit: '', label: 'реализованных проектов' },
        { number: '8', unit: '', label: 'городов с дилерами' }
      ],
      cta: {
        title: 'Готовы к сотрудничеству?',
        text: 'Свяжитесь с нами для расчёта вашего проекта',
        button: 'Связаться'
      },
      slide2: {
        company: 'ТОО «ГИПСОВИНИЛ»',
        subtitle: 'Первый и специализированный завод по производству гипсовиниловых панелей в Казахстане',
        badge: 'ПРОФЕССИОНАЛЬНЫЕ РЕШЕНИЯ',
        badgeText: 'для застройщиков, проектировщиков, коммерческих и государственных объектов'
      },
      service360: {
        title: 'НАШ СЕРВИС 360°',
        items: [
          { icon: BadgePercent, title: 'Гибкая программа скидок', text: 'От объёма — «больше партия — меньше цена»' },
          { icon: HardHat, title: 'Бесплатный шеф-монтаж', text: 'И технический надзор на всех этапах' },
          { icon: Truck, title: 'Выезд с полноразмерными образцами', text: 'И консультацией на объект' },
          { icon: Palette, title: 'Дизайнерские решения', text: 'Готовые капсулы с решениями под ваш объект от дизайнера' },
          { icon: Warehouse, title: 'Полная готовность к большим проектам', text: 'Производим 2000 м² за 24 часа и держим более 5000 м² в наличии' },
          { icon: Calculator, title: 'Собственный проектный отдел', text: 'Просчитает ваш объект без лишних трат' }
        ]
      },
      slide4: {
        title: 'НАША МИССИЯ',
        text: 'Помогать Казахстану строиться красиво. Делать современный дизайн доступным, быстрым и чистым — в школах, больницах, офисах, государственных зданиях, жилых комплексах и коммерческих пространствах.',
        quote: '«Мы верим, что качественная среда влияет на мышление и поведение человека. Наша работа — это вклад в развитие архитектурной культуры и стандартов нового времени.»'
      },
      slide14: {
        title: 'МОЩНАЯ И СОВРЕМЕННАЯ ПРОИЗВОДСТВЕННАЯ ПЛАТФОРМА',
        subtitle: 'ОСНОВА КОМПАНИИ',
        cycleTitle: 'Цикл производства под строгим контролем:',
        cycle: ['Приемка сырья', 'Промышленное ламинирование', 'Контроль адгезии', 'Упаковка и паллетирование', 'Отгрузка'],
        certTitle: 'НАШИ ПАНЕЛИ ПРОШЛИ ВСЕ ИСПЫТАНИЯ И ПОЛНУЮ СЕРТИФИКАЦИЮ',
        certs: [
          'Класс горючести Г1 — слабогорючие панели',
          'Занесены в реестр КазНИИСА и АГСК 3 — имеется сертификат СТ KZ',
          'Влагостойкость — применимы даже в бассейнах',
          'Одобрено Санэпидемстанцией — панели с моющимся покрытием',
          'Экологичность — безопасно для любых помещений'
        ],
        stats: [
          { number: '1 000 000 м²', label: 'в год — общая производственная мощность' },
          { number: '5 000 м²', label: 'готовой продукции на складе' },
          { number: '2 000 м²', label: 'за сутки — скорость изготовления' },
          { number: '30 дней', label: 'на монтаж 10 000 м²' }
        ]
      }
    },
    kz: {
      header: {
        title: 'Компания туралы',
        subtitle: 'Қазақстандағы гипсвинил панельдерін өндірудің алғашқы және мамандандырылған зауыты'
      },
      downloadPresentation: 'Компания туралы презентацияны жүктеу',
      mission: {
        title: 'Біздің миссиямыз',
        text: 'Қазақстанға әдемі салуға көмектесу. Заманауи дизайнды қолжетімді, жылдам және таза ету.',
        subtext: 'Біз сапалы орта адамның ойлауы мен мінез-құлқына әсер етеді деп сенеміз.'
      },
      gallery: {
        title: 'Біздің өндіріс және жобалар',
        items: [
          {
            image: 'https://images.unsplash.com/photo-1764185800646-f75f7e16e465?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxmYWN0b3J5JTIwcHJvZHVjdGlvbiUyMGxpbmUlMjBtYW51ZmFjdHVyaW5nfGVufDB8fHx8MTc3NDQ0MDM5OHww&ixlib=rb-4.1.0&q=85',
            title: 'Өндіріс желісі',
            subtitle: 'АЭА Астана'
          },
          {
            image: 'https://images.unsplash.com/photo-1750189953388-5ef155c12369?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBpbnRlcmlvciUyMGRlc2lnbiUyMGNsZWFuJTIwd2FsbHN8ZW58MHx8fHwxNzc0NDQwMzk0fDA&ixlib=rb-4.1.0&q=85',
            title: 'Дәліздер және холлдар',
            subtitle: 'Дайын шешімдер'
          },
          {
            image: 'https://images.pexels.com/photos/245219/pexels-photo-245219.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
            title: 'Кеңсе кеңістігі',
            subtitle: 'Заманауи дизайн'
          }
        ]
      },
      advantages: {
        title: 'Бізді неге таңдайды',
        items: [
          { icon: Factory, title: 'Өз өндірісі', text: 'Астана АЭА-да делдалсыз толық цикл. СТ KZ сертификаты.' },
          { icon: Award, title: 'Сала көшбасшылары', text: 'Гипсвинил панельдері саласында жұмыс істейтін алғашқы бренд.' },
          { icon: Shield, title: 'Сертификатталған өнім', text: 'Қауіпсіз, СЭС және өрт қызметі мақұлдаған.' },
          { icon: Users, title: '360° қызмет', text: 'Кеңестен шеф-монтаж және визуализацияға дейін.' },
          { icon: Package, title: 'Ірі жобаларға дайындық', text: '24 сағатта 2000 м². Қоймада 5000 м².' },
          { icon: Wrench, title: 'Жобалау бөлімі', text: 'Объектіңізді артық шығынсыз есептейміз.' }
        ]
      },
      product: {
        title: 'Инновациялық материал',
        subtitle: 'Ескірген әдістерге технологиялық балама',
        description: 'Гипсвинил панельдері — әлсіз жанатын плита мен тозуға төзімді ПВХ жабынның үйлесімі.',
        features: [
          { icon: Zap, text: 'Әрлеуді 5 есе жылдамдатады' },
          { icon: 'FlameStrikethrough', text: 'Жану класы Г1' },
          { icon: Droplets, text: 'Ылғалға төзімді' },
          { icon: CheckCircle2, text: 'СЭС мақұлдаған' }
        ],
        replaces: 'Алмастырады: бояу, сылақ, шпаклевка, бамбук панельдері, ЛДСП/МДФ',
        height: 'Панель биіктігі 3 м дейін'
      },
      stats: [
        { number: '1 млн', unit: 'м²', label: 'жылдық өндірістік қуат' },
        { number: '2 000', unit: 'м²', label: 'тәулігіне өндіру' },
        { number: '100+', unit: '', label: 'жүзеге асырылған жобалар' },
        { number: '8', unit: '', label: 'дилерлік қалалар' }
      ],
      cta: {
        title: 'Ынтымақтастыққа дайынсыз ба?',
        text: 'Жобаңызды есептеу үшін бізбен байланысыңыз',
        button: 'Байланысу'
      },
      slide2: {
        company: '«ГИПСОВИНИЛ» ЖШС',
        subtitle: 'Қазақстандағы гипсвинил панельдерін өндірудің алғашқы және мамандандырылған зауыты',
        badge: 'КӘСІБИ ШЕШІМДЕР',
        badgeText: 'құрылысшылар, жобалаушылар, коммерциялық және мемлекеттік объектілер үшін'
      },
      service360: {
        title: 'БІЗДІҢ СЕРВИС 360°',
        items: [
          { icon: BadgePercent, title: 'Икемді жеңілдік бағдарламасы', text: 'Көлемнен — «партия көп — баға аз»' },
          { icon: HardHat, title: 'Тегін шеф-монтаж', text: 'Және барлық кезеңдерде техникалық бақылау' },
          { icon: Truck, title: 'Толық өлшемді үлгілермен шығу', text: 'Және объектке кеңес беру' },
          { icon: Palette, title: 'Дизайнерлік шешімдер', text: 'Дизайнерден объектіңізге дайын шешімдер' },
          { icon: Warehouse, title: 'Ірі жобаларға толық дайындық', text: '24 сағатта 2000 м² өндіреміз, 5000 м² дайын өнім қоймада' },
          { icon: Calculator, title: 'Өз жобалау бөлімі', text: 'Объектіңізді артық шығынсыз есептейді' }
        ]
      },
      slide4: {
        title: 'БІЗДІҢ МИССИЯМЫЗ',
        text: 'Қазақстанға әдемі салуға көмектесу. Заманауи дизайнды қолжетімді, жылдам және таза ету — мектептерде, ауруханаларда, кеңселерде, мемлекеттік ғимараттарда, тұрғын үй кешендерінде және коммерциялық кеңістіктерде.',
        quote: '«Біз сапалы орта адамның ойлауы мен мінез-құлқына әсер етеді деп сенеміз. Біздің жұмысымыз — сәулет мәдениеті мен жаңа уақыт стандарттарын дамытуға үлес.»'
      },
      slide14: {
        title: 'ҚУАТТЫ ЖӘНЕ ЗАМАНАУИ ӨНДІРІСТІК ПЛАТФОРМА',
        subtitle: 'КОМПАНИЯНЫҢ НЕГІЗІ',
        cycleTitle: 'Қатаң бақылаудағы өндіріс циклі:',
        cycle: ['Шикізатты қабылдау', 'Өнеркәсіптік ламинаттау', 'Адгезияны бақылау', 'Орау және паллеттеу', 'Жөнелту'],
        certTitle: 'БІЗДІҢ ПАНЕЛЬДЕР БАРЛЫҚ СЫНАҚТАН ЖӘНЕ ТОЛЫҚ СЕРТИФИКАТТАУДАН ӨТТІ',
        certs: [
          'Жану класы Г1 — әлсіз жанатын панельдер',
          'ҚазҒЗСЗИ және АҒСК 3 тізіліміне енгізілген — СТ KZ сертификаты бар',
          'Ылғалға төзімділік — бассейндерде де қолданылады',
          'Санэпидемстанция мақұлдаған — жуылатын жабыны бар панельдер',
          'Экологиялық тазалық — кез келген бөлме үшін қауіпсіз'
        ],
        stats: [
          { number: '1 000 000 м²', label: 'жылына — жалпы өндірістік қуат' },
          { number: '5 000 м²', label: 'дайын өнім қоймада' },
          { number: '2 000 м²', label: 'тәулігіне — өндіру жылдамдығы' },
          { number: '30 күн', label: '10 000 м² монтажға' }
        ]
      }
    },
    en: {
      header: {
        title: 'About Company',
        subtitle: 'The first and specialized plant for production of gypsum-vinyl panels in Kazakhstan'
      },
      downloadPresentation: 'Download Company Presentation',
      mission: {
        title: 'Our Mission',
        text: 'Help Kazakhstan build beautifully. Make modern design accessible, fast and clean.',
        subtext: 'We believe that quality environment affects human thinking and behavior.'
      },
      gallery: {
        title: 'Our Production and Projects',
        items: [
          {
            image: 'https://images.unsplash.com/photo-1764185800646-f75f7e16e465?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxmYWN0b3J5JTIwcHJvZHVjdGlvbiUyMGxpbmUlMjBtYW51ZmFjdHVyaW5nfGVufDB8fHx8MTc3NDQ0MDM5OHww&ixlib=rb-4.1.0&q=85',
            title: 'Production Line',
            subtitle: 'SEZ Astana'
          },
          {
            image: 'https://images.unsplash.com/photo-1750189953388-5ef155c12369?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBpbnRlcmlvciUyMGRlc2lnbiUyMGNsZWFuJTIwd2FsbHN8ZW58MHx8fHwxNzc0NDQwMzk0fDA&ixlib=rb-4.1.0&q=85',
            title: 'Corridors and Halls',
            subtitle: 'Ready Solutions'
          },
          {
            image: 'https://images.pexels.com/photos/245219/pexels-photo-245219.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
            title: 'Office Spaces',
            subtitle: 'Modern Design'
          }
        ]
      },
      advantages: {
        title: 'Why Choose Us',
        items: [
          { icon: Factory, title: 'Own Production', text: 'Full cycle in Astana SEZ without intermediaries. ST KZ certificate.' },
          { icon: Award, title: 'Industry Leaders', text: 'First brand working exclusively in gypsum-vinyl panels niche.' },
          { icon: Shield, title: 'Certified Products', text: 'Safe, approved by SES and fire services. Class G1.' },
          { icon: Users, title: '360° Service', text: 'From consultation to installation and visualization.' },
          { icon: Package, title: 'Ready for Large Projects', text: '2000 m² in 24 hours. Over 5000 m² in stock.' },
          { icon: Wrench, title: 'Design Department', text: 'We calculate your project without extra costs.' }
        ]
      },
      product: {
        title: 'Innovative Material',
        subtitle: 'Technological alternative to outdated finishing methods',
        description: 'Gypsum-vinyl panels combine low-flammability board and wear-resistant, washable decorative PVC coating.',
        features: [
          { icon: Zap, text: 'Speeds up finishing 5 times' },
          { icon: 'FlameStrikethrough', text: 'Fire class G1 (low flammable)' },
          { icon: Droplets, text: 'Moisture resistant - suitable for pools' },
          { icon: CheckCircle2, text: 'SES approved, washable coating' }
        ],
        replaces: 'Replaces: painting, plastering, putty, bamboo panels, LDSP/MDF',
        height: 'Panel height up to 3m - minimal joints, maximum aesthetics'
      },
      stats: [
        { number: '1 mln', unit: 'm²', label: 'production capacity per year' },
        { number: '2 000', unit: 'm²', label: 'manufacturing per day' },
        { number: '100+', unit: '', label: 'completed projects' },
        { number: '8', unit: '', label: 'cities with dealers' }
      ],
      cta: {
        title: 'Ready to Cooperate?',
        text: 'Contact us to calculate your project',
        button: 'Contact Us'
      },
      slide2: {
        company: 'GIPSOVINIL LLP',
        subtitle: 'The first and specialized plant for production of gypsum-vinyl panels in Kazakhstan',
        badge: 'PROFESSIONAL SOLUTIONS',
        badgeText: 'for developers, designers, commercial and government facilities'
      },
      service360: {
        title: 'OUR 360° SERVICE',
        items: [
          { icon: BadgePercent, title: 'Flexible Discount Program', text: 'Volume-based — "bigger order — lower price"' },
          { icon: HardHat, title: 'Free Installation Supervision', text: 'And technical oversight at all stages' },
          { icon: Truck, title: 'On-site Visit with Full-size Samples', text: 'And consultation at your facility' },
          { icon: Palette, title: 'Design Solutions', text: 'Ready-made design capsules for your project from our designer' },
          { icon: Warehouse, title: 'Full Readiness for Large Projects', text: 'We produce 2,000 m² in 24 hours and keep 5,000+ m² in stock' },
          { icon: Calculator, title: 'In-house Design Department', text: 'Calculate your project without extra costs' }
        ]
      },
      slide4: {
        title: 'OUR MISSION',
        text: 'Help Kazakhstan build beautifully. Make modern design accessible, fast and clean — in schools, hospitals, offices, government buildings, residential complexes and commercial spaces.',
        quote: '"We believe that a quality environment affects human thinking and behavior. Our work is a contribution to the development of architectural culture and standards of the new era."'
      },
      slide14: {
        title: 'POWERFUL AND MODERN PRODUCTION PLATFORM',
        subtitle: 'THE FOUNDATION OF THE COMPANY',
        cycleTitle: 'Production cycle under strict control:',
        cycle: ['Raw material acceptance', 'Industrial lamination', 'Adhesion control', 'Packaging and palletizing', 'Shipping'],
        certTitle: 'OUR PANELS HAVE PASSED ALL TESTS AND FULL CERTIFICATION',
        certs: [
          'Fire class G1 — low-flammable panels',
          'Listed in KazNIISA and AGSK 3 registry — ST KZ certificate available',
          'Moisture resistance — applicable even in swimming pools',
          'Approved by Sanitary and Epidemiological Station — washable coating',
          'Eco-friendly — safe for any premises'
        ],
        stats: [
          { number: '1,000,000 m²', label: 'per year — total production capacity' },
          { number: '5,000 m²', label: 'of finished products in stock' },
          { number: '2,000 m²', label: 'per day — manufacturing speed' },
          { number: '30 days', label: 'to install 10,000 m²' }
        ]
      }
    },
    ar: {
      header: {
        title: 'عن الشركة',
        subtitle: 'أول مصنع متخصص لإنتاج ألواح الجبس الفينيل في كازاخستان'
      },
      downloadPresentation: 'تحميل عرض الشركة',
      mission: {
        title: 'مهمتنا',
        text: 'مساعدة كازاخستان على البناء بجمال. جعل التصميم العصري في متناول الجميع، بسرعة ونظافة.',
        subtext: 'نؤمن بأن البيئة عالية الجودة تؤثر على تفكير الإنسان وسلوكه.'
      },
      gallery: {
        title: 'إنتاجنا ومشاريعنا',
        items: [
          {
            image: 'https://images.unsplash.com/photo-1764185800646-f75f7e16e465?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxmYWN0b3J5JTIwcHJvZHVjdGlvbiUyMGxpbmUlMjBtYW51ZmFjdHVyaW5nfGVufDB8fHx8MTc3NDQ0MDM5OHww&ixlib=rb-4.1.0&q=85',
            title: 'خط الإنتاج',
            subtitle: 'المنطقة الاقتصادية الخاصة أستانا'
          },
          {
            image: 'https://images.unsplash.com/photo-1750189953388-5ef155c12369?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBpbnRlcmlvciUyMGRlc2lnbiUyMGNsZWFuJTIwd2FsbHN8ZW58MHx8fHwxNzc0NDQwMzk0fDA&ixlib=rb-4.1.0&q=85',
            title: 'الممرات والقاعات',
            subtitle: 'حلول جاهزة'
          },
          {
            image: 'https://images.pexels.com/photos/245219/pexels-photo-245219.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
            title: 'المساحات المكتبية',
            subtitle: 'تصميم عصري'
          }
        ]
      },
      advantages: {
        title: 'لماذا تختارنا',
        items: [
          { icon: Factory, title: 'إنتاج خاص', text: 'دورة إنتاج متكاملة في المنطقة الاقتصادية الخاصة بأستانا بدون وسطاء. شهادة ST KZ.' },
          { icon: Award, title: 'رواد القطاع', text: 'أول علامة تجارية تعمل حصرياً في مجال ألواح الجبس الفينيل.' },
          { icon: Shield, title: 'منتجات معتمدة', text: 'آمنة، معتمدة من الرقابة الصحية وخدمات الإطفاء. فئة G1.' },
          { icon: Users, title: 'خدمة 360°', text: 'من الاستشارة إلى التركيب والتصور المرئي.' },
          { icon: Package, title: 'جاهزون للمشاريع الكبرى', text: '2000 م² في 24 ساعة. أكثر من 5000 م² في المخزون.' },
          { icon: Wrench, title: 'قسم التصميم', text: 'نحسب مشروعك دون تكاليف إضافية.' }
        ]
      },
      product: {
        title: 'مواد مبتكرة',
        subtitle: 'بديل تقني عن أساليب التشطيب القديمة',
        description: 'تجمع ألواح الجبس الفينيل بين لوح منخفض الاشتعال وطبقة زخرفية من PVC مقاومة للاهتراء وقابلة للغسيل.',
        features: [
          { icon: Zap, text: 'تُسرّع أعمال التشطيب 5 مرات' },
          { icon: 'FlameStrikethrough', text: 'فئة الحريق G1 (منخفض الاشتعال)' },
          { icon: Droplets, text: 'مقاوم للرطوبة - مناسب لحمامات السباحة' },
          { icon: CheckCircle2, text: 'معتمد صحياً، طلاء قابل للغسيل' }
        ],
        replaces: 'يحل محل: الدهان، والجص، والمعجون، وألواح الخيزران، وLDSP/MDF',
        height: 'ارتفاع اللوح يصل إلى 3م - حد أدنى من الوصلات وأقصى قدر من الجماليات'
      },
      stats: [
        { number: '1 مليون', unit: 'م²', label: 'طاقة إنتاجية سنوية' },
        { number: '2 000', unit: 'م²', label: 'إنتاج يومي' },
        { number: '+100', unit: '', label: 'مشروع منجز' },
        { number: '8', unit: '', label: 'مدن بها موزعون' }
      ],
      cta: {
        title: 'هل أنتم مستعدون للتعاون؟',
        text: 'تواصلوا معنا لحساب مشروعكم',
        button: 'تواصل معنا'
      },
      slide2: {
        company: 'GIPSOVINIL LLP',
        subtitle: 'أول مصنع متخصص لإنتاج ألواح الجبس الفينيل في كازاخستان',
        badge: 'حلول احترافية',
        badgeText: 'للمطورين والمصممين والمنشآت التجارية والحكومية'
      },
      service360: {
        title: 'خدمتنا الشاملة 360°',
        items: [
          { icon: BadgePercent, title: 'برنامج خصم مرن', text: 'مرتبط بالحجم — "كلما زاد الطلب، انخفض السعر"' },
          { icon: HardHat, title: 'إشراف تركيب مجاني', text: 'ومتابعة فنية في جميع المراحل' },
          { icon: Truck, title: 'زيارة ميدانية بعينات بالحجم الطبيعي', text: 'واستشارة في موقع مشروعكم' },
          { icon: Palette, title: 'حلول تصميمية', text: 'كبسولات تصميم جاهزة لمشروعكم من مصممنا المتخصص' },
          { icon: Warehouse, title: 'جاهزية كاملة للمشاريع الكبرى', text: 'ننتج 2,000 م² في 24 ساعة ونحتفظ بأكثر من 5,000 م² في المخزون' },
          { icon: Calculator, title: 'قسم تصميم داخلي', text: 'نحسب مشروعكم دون تكاليف إضافية' }
        ]
      },
      slide4: {
        title: 'مهمتنا',
        text: 'مساعدة كازاخستان على البناء بجمال. جعل التصميم العصري في متناول الجميع، بسرعة ونظافة — في المدارس والمستشفيات والمكاتب والمباني الحكومية والمجمعات السكنية والفضاءات التجارية.',
        quote: '"نؤمن بأن البيئة عالية الجودة تؤثر على تفكير الإنسان وسلوكه. عملنا مساهمة في تطوير الثقافة المعمارية ومعايير العصر الجديد."'
      },
      slide14: {
        title: 'منصة إنتاجية قوية وعصرية',
        subtitle: 'الركيزة الأساسية للشركة',
        cycleTitle: 'دورة إنتاج تحت رقابة صارمة:',
        cycle: ['استلام المواد الخام', 'التصفيح الصناعي', 'مراقبة الالتصاق', 'التعبئة والتخزين على منصات', 'الشحن'],
        certTitle: 'اجتازت ألواحنا جميع الاختبارات والحصول على الاعتماد الكامل',
        certs: [
          'فئة الحريق G1 — ألواح منخفضة الاشتعال',
          'مُدرجة في سجل KazNIISA وAGSK 3 — شهادة ST KZ متاحة',
          'مقاومة الرطوبة — صالحة للاستخدام حتى في حمامات السباحة',
          'معتمدة من الرقابة الصحية والوبائية — طلاء قابل للغسيل',
          'صديقة للبيئة — آمنة لأي نوع من المباني'
        ],
        stats: [
          { number: '1,000,000 م²', label: 'سنوياً — إجمالي الطاقة الإنتاجية' },
          { number: '5,000 م²', label: 'من المنتجات الجاهزة في المخزون' },
          { number: '2,000 م²', label: 'يومياً — سرعة التصنيع' },
          { number: '30 يوماً', label: 'لتركيب 10,000 م²' }
        ]
      }
    }
  };

  const t = content[language] || content.ru;

  return (
    <div className="pt-20 bg-[#061d36]" data-testid="about-page">
      <SEOHead 
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
      />

      {/* Slide 2 - Company Intro (from Presentation) */}
      <section className="bg-[#061d36]" data-testid="about-slide-2">
        <div className="container mx-auto px-4 max-w-6xl">
          <img 
            src="/images/slide2_about.webp" 
            alt={t.slide2.company}
            className="w-full h-auto rounded-2xl shadow-lg"
          />
        </div>
      </section>

      {/* Slide 4 - Big Idea */}
      <section className="py-20 bg-[#F5F5F0]" data-testid="about-slide-4">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <h1 className="text-3xl md:text-5xl font-bold text-[#061d36] mb-8">{t.slide4.title}</h1>
          <p className="text-xl md:text-2xl text-[#061d36] leading-relaxed mb-8">{t.slide4.text}</p>
          <blockquote className="text-lg text-[#061d36]/70 italic border-l-4 border-[#061d36] pl-6 text-left max-w-2xl mx-auto">
            {t.slide4.quote}
          </blockquote>
        </div>
      </section>

      {/* Service 360° */}
      <section className="py-16 bg-[#F5F5F0]" data-testid="about-service-360">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-[#061d36] text-center mb-12">{t.service360.title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.service360.items.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all" data-testid={`service-360-item-${idx}`}>
                  <div className="w-12 h-12 bg-[#061d36] rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-[#061d36] mb-2">{item.title}</h3>
                  <p className="text-[#061d36]/70">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Slide 14 - Production Platform */}
      <section className="py-16 bg-[#061d36] text-white" data-testid="about-slide-14">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-bold mb-2">{t.slide14.title}</h2>
            <p className="text-lg text-blue-200">{t.slide14.subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Production Cycle */}
            <div className="bg-white/10 rounded-2xl p-6">
              <h3 className="text-lg font-bold mb-4">{t.slide14.cycleTitle}</h3>
              <div className="space-y-3">
                {t.slide14.cycle.map((step, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold">{idx + 1}</span>
                    </div>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div className="bg-white/10 rounded-2xl p-6">
              <h3 className="text-lg font-bold mb-4">{t.slide14.certTitle}</h3>
              <div className="space-y-3">
                {t.slide14.certs.map((cert, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-blue-100">{cert}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="pt-0 pb-12 bg-[#061d36]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-8">{t.advantages.title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {t.advantages.items.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="bg-[#1a3a5c] rounded-2xl p-6 hover:bg-[#1f4a6e] transition-colors">
                  <div className="w-14 h-14 bg-[#8b4444] rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-white/70">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-[#061d36]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {t.stats.map((stat, idx) => (
              <div key={idx} className="text-center text-white">
                <div className="text-4xl md:text-5xl font-bold mb-1">
                  {stat.number}<span className="text-2xl text-blue-300 ml-1">{stat.unit}</span>
                </div>
                <div className="text-sm text-blue-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pt-20 py-8 pb-16 bg-[#061d36] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.cta.title}</h2>
          <p className="text-xl text-blue-200 mb-8">{t.cta.text}</p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-colors"
          >
            {t.cta.button}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
